import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserCheck, Clock, Check, X, Search, Filter, Mail, Calendar, Building2, User, RefreshCw } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import { db, collection, query, getDocs, doc, updateDoc, serverTimestamp, getDoc, where } from '../lib/firebase';
import type { UserProfile, Agency } from '../lib/firebase';

export default function UserApproval() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    // Vérifier si l'utilisateur est admin ou super admin
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    if (!isAdmin()) {
      navigate('/app');
      return;
    }
    
    loadData();
    
    // Recharger les données toutes les 30 secondes pour voir les nouveaux comptes
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [currentUser, userProfile, navigate]);

  // Fonction pour vérifier si l'utilisateur est admin
  const isAdmin = () => {
    return currentUser?.email === 'hosni@swipe-upp.com' || userProfile?.userRole === 'admin';
  };

  const migrateExistingUsers = async () => {
    try {
      setIsMigrating(true);
      
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const updates = [];
      
      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();

        // Si l'utilisateur n'a pas de champ status
        if (!userData.status) {
          // Le super admin est automatiquement approuvé
          const status = userData.email === 'hosni@swipe-upp.com' ? 'approved' : 'pending';
          
          updates.push(
            updateDoc(doc(db, 'users', userDoc.id), {
              status: status,
              updatedAt: serverTimestamp()
            })
          );
        }
      }
      
      await Promise.all(updates);
      await loadData();
    } catch (error) {
      console.error('Error migrating users:', error);
    } finally {
      setIsMigrating(false);
    }
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setSuccessMessage(null);
      
      if (!currentUser) return;
      
      let usersQuery;
      
      // Si c'est le super admin, il voit tous les utilisateurs
      if (currentUser.email === 'hosni@swipe-upp.com') {
        usersQuery = query(collection(db, 'users'));
      } 
      // Sinon, l'admin ne voit que les utilisateurs de son agence
      else if (userProfile?.agencyId) {
        usersQuery = query(
          collection(db, 'users'), 
          where('agencyId', '==', userProfile.agencyId)
        );
      } else {
        setError("Vous n'avez pas d'agence associée à votre compte");
        setIsLoading(false);
        return;
      }
      
      const usersSnapshot = await getDocs(usersQuery);
      const usersData = usersSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as UserProfile[];
      
      // Assigner un statut par défaut aux utilisateurs qui n'en ont pas
      const usersWithStatus = usersData.filter(user => user.id !== currentUser.uid).map(user => ({
        ...user,
        status: user.status || (user.email === 'hosni@swipe-upp.com' ? 'approved' : 'pending')
      }));
      
      // Filtrer pour exclure l'utilisateur actuel et le super admin (sauf si c'est le super admin qui regarde)
      const filteredUsers = usersWithStatus.filter(user => {
        if (user.id === currentUser.uid) return false;
        if (user.email === 'hosni@swipe-upp.com' && currentUser.email !== 'hosni@swipe-upp.com') return false;
        return true;
      });
      
      setUsers(filteredUsers);

      // Fetch all agencies
      let agenciesQuery;
      
      if (currentUser.email === 'hosni@swipe-upp.com') {
        agenciesQuery = query(collection(db, 'agencies'));
      } else if (userProfile?.agencyId) {
        agenciesQuery = query(
          collection(db, 'agencies'),
          where('id', '==', userProfile.agencyId)
        );
      } else {
        agenciesQuery = query(collection(db, 'agencies'), where('id', '==', 'non-existent-id'));
      }
      
      const agenciesSnapshot = await getDocs(agenciesQuery);
      const agenciesData = agenciesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Agency[];
      setAgencies(agenciesData);
      
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserAction = async (userId: string, action: 'approve' | 'reject') => {
    try {
      setIsProcessing(true);
      setSuccessMessage(null);
      
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        status: action === 'approve' ? 'approved' : 'rejected',
        updatedAt: serverTimestamp()
      });

      // Afficher un message de succès
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      setSuccessMessage(
        `L'utilisateur ${userData?.firstName || ''} ${userData?.lastName || ''} a été ${action === 'approve' ? 'approuvé' : 'rejeté'} avec succès.`
      );

      await loadData();
      setIsDetailModalOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error updating user status:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDate = (date: Date | { seconds: number } | undefined) => {
    if (!date) return 'Date inconnue';
    
    if ('seconds' in date) {
      return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-500/20 text-green-300 border-green-500/20';
      case 'rejected':
        return 'bg-red-500/20 text-red-300 border-red-500/20';
      default:
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/20';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'approved':
        return 'Approuvé';
      case 'rejected':
        return 'Rejeté';
      default:
        return 'En attente';
    }
  };

  const getAgencyName = (agencyId: string) => {
    const agency = agencies.find(a => a.id === agencyId);
    return agency?.name || 'Agence inconnue';
  };

  const filteredUsers = users.filter(user => {
    // Exclure le super admin de la liste
    if (user.email === 'hosni@swipe-upp.com') return false;
    
    const matchesSearch = 
      (user.firstName || '').toLowerCase().includes(search.toLowerCase()) ||
      (user.lastName || '').toLowerCase().includes(search.toLowerCase()) ||
      user.email.toLowerCase().includes(search.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const pendingCount = users.filter(u => u.status === 'pending').length;
  const approvedCount = users.filter(u => u.status === 'approved').length;
  const rejectedCount = users.filter(u => u.status === 'rejected').length;
  const usersWithoutStatus = users.filter(u => !u.status).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-purple to-accent-blue flex items-center justify-center">
            <UserCheck className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">
              {currentUser?.email === 'hosni@swipe-upp.com' 
                ? "Approbation des utilisateurs (Super Admin)" 
                : "Approbation des utilisateurs de votre agence"}
            </h1>
            <p className="text-gray-400">
              {currentUser?.email === 'hosni@swipe-upp.com'
                ? "Gérez les demandes d'accès à la plateforme pour toutes les agences"
                : "Gérez les demandes d'accès à votre agence"}
            </p>
          </div>
        </div>
        {currentUser?.email === 'hosni@swipe-upp.com' && usersWithoutStatus > 0 && (
          <button
            onClick={migrateExistingUsers}
            disabled={isMigrating}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${isMigrating ? 'animate-spin' : ''}`} />
            {isMigrating ? 'Migration en cours...' : `Migrer ${usersWithoutStatus} compte(s) ancien(s)`}
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-dark-50 rounded-xl p-6 border border-dark-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-medium text-white">En attente</h3>
            <Clock className="w-5 h-5 text-yellow-400" />
          </div>
          <p className="text-2xl font-bold text-white">{pendingCount}</p>
        </div>
        <div className="bg-dark-50 rounded-xl p-6 border border-dark-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-medium text-white">Approuvés</h3>
            <Check className="w-5 h-5 text-green-400" />
          </div>
          <p className="text-2xl font-bold text-white">{approvedCount}</p>
        </div>
        <div className="bg-dark-50 rounded-xl p-6 border border-dark-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-medium text-white">Rejetés</h3>
            <X className="w-5 h-5 text-red-400" />
          </div>
          <p className="text-2xl font-bold text-white">{rejectedCount}</p>
        </div>
      </div>

      {successMessage && (
        <div className="bg-green-900/50 border border-green-500/20 text-green-200 px-4 py-3 rounded-lg mb-6">
          <div className="flex items-center">
            <div className="py-1"><svg className="fill-current h-6 w-6 text-green-200 mr-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM6.7 9.29L9 11.6l4.3-4.3 1.4 1.42L9 14.4l-3.7-3.7 1.4-1.42z"/></svg></div>
            <div>{successMessage}</div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-900/50 border border-red-500/20 text-red-200 px-4 py-3 rounded-lg">
          <div className="flex items-center">
            <div className="py-1"><svg className="fill-current h-6 w-6 text-red-200 mr-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z"/></svg></div>
            <div>{error}</div>
          </div>
        </div>
      )}


      <div className="bg-dark-50 rounded-xl border border-dark-200 p-6">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un utilisateur..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 bg-dark-100 border-dark-200 text-white"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full sm:w-48 bg-dark-100 border-dark-200 text-white"
          >
            <option value="all">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="approved">Approuvés</option>
            <option value="rejected">Rejetés</option>
          </select>
        </div>

        {filteredUsers.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <UserCheck className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-white mb-2">Aucun utilisateur à approuver</h3>
            <p className="text-gray-400">Tous les utilisateurs de votre agence ont déjà été traités.</p>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={filteredUsers.length === 0 ? 'hidden' : ''}>
                <tr className="border-b border-dark-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Utilisateur</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Email</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Agence</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Date d'inscription</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Statut</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className={`divide-y divide-dark-200 ${filteredUsers.length === 0 ? 'hidden' : ''}`}>
                {filteredUsers.map((user) => (
                  <tr 
                    key={user.id} 
                    className="hover:bg-dark-100/50 cursor-pointer"
                    onClick={() => {
                      setSelectedUser(user);
                      setIsDetailModalOpen(true);
                    }}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-accent-blue/10 flex items-center justify-center">
                          <User className="w-4 h-4 text-accent-blue" />
                        </div>
                        <div>
                          <div className="text-white font-medium">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-sm text-gray-400">{user.role}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-400">{user.email}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-accent-blue" />
                        {user.agencyId ? (
                          <span className="text-gray-400">
                            {getAgencyName(user.agencyId)}
                          </span>
                        ) : (
                          <span className="text-red-400">Aucune agence</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-400">{formatDate(user.createdAt)}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(user.status)}`}>
                        {getStatusLabel(user.status)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      {user.status === 'pending' && (
                        <div className="flex items-center gap-2 justify-end">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUserAction(user.id!, 'approve');
                            }}
                            disabled={isProcessing}
                            className="p-1 text-green-400 hover:text-green-300 disabled:opacity-50"
                            title="Approuver"
                          >
                            <Check className="w-5 h-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUserAction(user.id!, 'reject');
                            }}
                            disabled={isProcessing}
                            className="p-1 text-red-400 hover:text-red-300 disabled:opacity-50"
                            title="Rejeter"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <Dialog
        open={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-dark-50 rounded-xl max-w-2xl w-full mx-4 p-6 border border-dark-200">
            {selectedUser && (
              <>
                <div className="absolute top-4 right-4">
                  <button
                    onClick={() => setIsDetailModalOpen(false)}
                    className="text-gray-400 hover:text-gray-300"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-white">
                        {selectedUser.firstName} {selectedUser.lastName}
                      </h2>
                      <p className="text-gray-400">{selectedUser.email}</p>
                    </div>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusBadge(selectedUser.status)}`}>
                      {getStatusLabel(selectedUser.status)}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-dark-100 rounded-lg p-4">
                      <h3 className="text-lg font-medium text-white mb-3">Informations</h3>
                      <div className="space-y-3">
                        <div>
                          <span className="text-gray-400">Rôle :</span>
                          <span className="text-white ml-2">{selectedUser.role}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Type de compte :</span>
                          <span className="text-white ml-2">
                            {selectedUser.userRole === 'admin' ? 'Administrateur' : 'Utilisateur'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-400">Date d'inscription :</span>
                          <span className="text-white ml-2">{formatDate(selectedUser.createdAt)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-dark-100 rounded-lg p-4">
                      <h3 className="text-lg font-medium text-white mb-3">Agence</h3>
                      <div className="space-y-3">
                        {selectedUser.agencyId ? (
                          <>
                            <div className="flex items-center gap-2">
                              <Building2 className="w-5 h-5 text-accent-blue" />
                              <span className="text-white">{getAgencyName(selectedUser.agencyId)}</span>
                            </div> 
                            <div className="text-sm text-gray-400">
                              ID: {selectedUser.agencyId}
                            </div>
                          </>
                        ) : (
                          <p className="text-gray-400">Aucune agence associée</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {selectedUser.status === 'pending' && (
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => handleUserAction(selectedUser.id!, 'reject')}
                        disabled={isProcessing}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                      >
                        <X className="w-4 h-4" />
                        Rejeter
                      </button>
                      <button
                        onClick={() => handleUserAction(selectedUser.id!, 'approve')}
                        disabled={isProcessing}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                      >
                        <Check className="w-4 h-4" />
                        Approuver
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </Dialog>
    </div>
  );
}